import { Hub, HubCallback } from '@aws-amplify/core';

/**
 * Merging a managed user into another of the person's users needs two
 * sessions at once: the API call is signed as the managed user and carries
 * the other user's access token. Amplify keeps a single session in storage
 * and signing in as the other user overwrites it, so the managed session is
 * copied aside before that sign-in and put back right after it. Starting
 * from the other user's session, that one is copied aside instead.
 *
 * Bootstrap plumbing, like `currentIdentity.ts`: it reads and writes the
 * Cognito keys Amplify keeps in localStorage.
 */

const COGNITO_PREFIX = 'CognitoIdentityServiceProvider.';
const LAST_AUTH_USER_SUFFIX = '.LastAuthUser';
const HOSTED_UI_KEY = 'amplify-signin-with-hostedUI';
export const IDENTITY_CLAIM_MERGE_KEY = 'identity-claim-merge';
// The other user's access token lasts an hour.
const MERGE_TTL_MS = 60 * 60 * 1000;

type CognitoSessionSnapshot = {
  lastAuthUserKey: string;
  username: string;
  items: Record<string, string>;
  hostedUI: string | null;
};

type PendingMerge =
  | {
      phase: 'awaiting-other';
      startedAt: number;
      managed: CognitoSessionSnapshot;
    }
  | {
      phase: 'awaiting-managed';
      startedAt: number;
      other: CognitoSessionSnapshot;
    }
  | {
      phase: 'ready';
      startedAt: number;
      managed: CognitoSessionSnapshot;
      other: CognitoSessionSnapshot;
    };

export type ReadyMerge = {
  managedUsername: string;
  otherUsername: string;
  otherAccessToken: string;
};

const storageKeys = (storage: Storage): string[] =>
  Array.from({ length: storage.length }, (_, index) =>
    storage.key(index)
  ).filter((key): key is string => key !== null);

const findLastAuthUserKey = (storage: Storage): string | undefined =>
  storageKeys(storage).find(
    (key) =>
      key.startsWith(COGNITO_PREFIX) && key.endsWith(LAST_AUTH_USER_SUFFIX)
  );

const currentUsername = (storage: Storage): string | null => {
  const key = findLastAuthUserKey(storage);
  return key ? storage.getItem(key) : null;
};

const snapshotCurrentSession = (
  storage: Storage
): CognitoSessionSnapshot | null => {
  const lastAuthUserKey = findLastAuthUserKey(storage);
  const username = lastAuthUserKey && storage.getItem(lastAuthUserKey);
  if (!lastAuthUserKey || !username) return null;
  const clientPrefix = lastAuthUserKey.slice(0, -LAST_AUTH_USER_SUFFIX.length);
  const userPrefix = `${clientPrefix}.${username}.`;
  const items = Object.fromEntries(
    storageKeys(storage)
      .filter((key) => key.startsWith(userPrefix))
      .map((key) => [key, storage.getItem(key) ?? ''])
  );
  return {
    lastAuthUserKey,
    username,
    items,
    hostedUI: storage.getItem(HOSTED_UI_KEY),
  };
};

const restoreSession = (storage: Storage, session: CognitoSessionSnapshot) => {
  Object.entries(session.items).forEach(([key, value]) =>
    storage.setItem(key, value)
  );
  storage.setItem(session.lastAuthUserKey, session.username);
  if (session.hostedUI === null) storage.removeItem(HOSTED_UI_KEY);
  else storage.setItem(HOSTED_UI_KEY, session.hostedUI);
};

const removeSession = (storage: Storage, session: CognitoSessionSnapshot) =>
  Object.keys(session.items).forEach((key) => storage.removeItem(key));

const accessToken = (session: CognitoSessionSnapshot): string | undefined =>
  Object.entries(session.items).find(([key]) =>
    key.endsWith(`.${session.username}.accessToken`)
  )?.[1];

// The app can still mount as the managed user on a page that is about to
// reload; the merge is left for the new page, and this dies with the old one.
let reloading = false;

export const reloadApp = () => {
  reloading = true;
  window.location.reload();
};

const readMerge = (storage: Storage): PendingMerge | null => {
  try {
    const raw = storage.getItem(IDENTITY_CLAIM_MERGE_KEY);
    return raw ? (JSON.parse(raw) as PendingMerge) : null;
  } catch {
    return null;
  }
};

const writeMerge = (storage: Storage, merge: PendingMerge) =>
  storage.setItem(IDENTITY_CLAIM_MERGE_KEY, JSON.stringify(merge));

const isExpired = (merge: PendingMerge, now: number) =>
  now - merge.startedAt > MERGE_TTL_MS;

/** Copies the managed user's session aside, before signing in as the other user. */
export const beginMergeSignIn = (
  storage: Storage = window.localStorage,
  now: number = Date.now()
): void => {
  const managed = snapshotCurrentSession(storage);
  if (!managed) throw new Error('No hay una sesión iniciada.');
  writeMerge(storage, { phase: 'awaiting-other', startedAt: now, managed });
};

/** Copies the other user's session aside, before signing in as the managed user. */
export const beginMergeSignInAsManaged = (
  storage: Storage = window.localStorage,
  now: number = Date.now()
): void => {
  const other = snapshotCurrentSession(storage);
  if (!other) throw new Error('No hay una sesión iniciada.');
  writeMerge(storage, { phase: 'awaiting-managed', startedAt: now, other });
};

/**
 * Once signed in with the missing session: leaves the managed one as the
 * current session and the other one copied aside. True when it did, and the
 * page has to reload so Amplify and the API credentials pick the managed
 * user up.
 */
export const settleMergeSignIn = (
  storage: Storage = window.localStorage,
  now: number = Date.now()
): boolean => {
  const merge = readMerge(storage);
  if (merge?.phase !== 'awaiting-other' && merge?.phase !== 'awaiting-managed')
    return false;
  if (isExpired(merge, now)) {
    storage.removeItem(IDENTITY_CLAIM_MERGE_KEY);
    return false;
  }
  const current = snapshotCurrentSession(storage);
  if (merge.phase === 'awaiting-other') {
    if (!current || current.username === merge.managed.username) return false;
    restoreSession(storage, merge.managed);
    writeMerge(storage, {
      phase: 'ready',
      startedAt: merge.startedAt,
      managed: merge.managed,
      other: current,
    });
    return true;
  }
  if (!current || current.username === merge.other.username) return false;
  writeMerge(storage, {
    phase: 'ready',
    startedAt: merge.startedAt,
    managed: current,
    other: merge.other,
  });
  return true;
};

/**
 * The merge that is ready to run, when the managed user is the current
 * session and this page is not reloading.
 */
export const readReadyMerge = (
  storage: Storage = window.localStorage,
  now: number = Date.now()
): ReadyMerge | null => {
  if (reloading) return null;
  const merge = readMerge(storage);
  if (merge?.phase !== 'ready') return null;
  if (isExpired(merge, now)) {
    abandonMerge(storage);
    return null;
  }
  const otherAccessToken = accessToken(merge.other);
  if (!otherAccessToken || currentUsername(storage) !== merge.managed.username)
    return null;
  return {
    managedUsername: merge.managed.username,
    otherUsername: merge.other.username,
    otherAccessToken,
  };
};

/** After the merge: the other user's session becomes the current one. */
export const completeMerge = (storage: Storage = window.localStorage): void => {
  const merge = readMerge(storage);
  if (merge?.phase !== 'ready') return;
  removeSession(storage, merge.managed);
  restoreSession(storage, merge.other);
  storage.removeItem(IDENTITY_CLAIM_MERGE_KEY);
};

/** Drops the merge and the other user's copied session; the managed one stays. */
export const abandonMerge = (storage: Storage = window.localStorage): void => {
  const merge = readMerge(storage);
  if (
    merge?.phase === 'ready' &&
    merge.other.username !== currentUsername(storage)
  )
    removeSession(storage, merge.other);
  storage.removeItem(IDENTITY_CLAIM_MERGE_KEY);
};

/**
 * Signing in with Google leaves the app through the hosted UI and comes back
 * with a new page; Amplify stores the Google session after the page loads.
 * Has to be listening before `Amplify.configure`, which handles that return.
 */
export const listenForHostedUIMergeSignIn = () => {
  const listener: HubCallback = ({ payload }) => {
    if (payload.event === 'cognitoHostedUI' && settleMergeSignIn()) reloadApp();
  };
  return Hub.listen('auth', listener);
};
